import { useCallback, useEffect, useMemo, useState } from "react";
import AdminSidebar from "./AdminSidebar";
import AdminTopbar from "./AdminTopbar";

const STORAGE_KEY = "allhomedesire:admin-shell:collapsed";
const MOBILE_BREAKPOINT = 992;

const isMobileViewport = () =>
  typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false;

const getInitialCollapsed = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);

  if (stored !== null) {
    return stored === "true";
  }

  return window.innerWidth < 1280;
};

const AdminLayoutShell = ({
  children,
  sections,
  homePath,
  logoutRedirect,
  titleGetter,
}) => {
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [collapsed, setCollapsed] = useState(getInitialCollapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const handleCloseDrawer = useCallback(() => setDrawerOpen(false), []);
  const handleOpenDrawer = useCallback(() => setDrawerOpen(true), []);
  const handleToggleCollapse = useCallback(
    () => setCollapsed((current) => !current),
    []
  );

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = isMobileViewport();
      setIsMobile(nextIsMobile);

      if (!nextIsMobile) {
        setDrawerOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, String(collapsed));
    }
  }, [collapsed]);

  const shellWidth = isMobile ? 0 : collapsed ? 88 : 272;
  const shellStyle = useMemo(
    () => ({
      "--admin-shell-sidebar-width": "272px",
      "--admin-shell-sidebar-collapsed-width": "88px",
      "--admin-shell-sidebar-current-width": `${shellWidth}px`,
    }),
    [shellWidth]
  );

  return (
    <div className="admin-shell" style={shellStyle}>
      <AdminSidebar
        sections={sections}
        homePath={homePath}
        collapsed={collapsed}
        isMobile={isMobile}
        drawerOpen={drawerOpen}
        onCloseDrawer={handleCloseDrawer}
      />

      <div className="admin-shell__viewport">
        <AdminTopbar
          titleGetter={titleGetter}
          homePath={homePath}
          logoutRedirect={logoutRedirect}
          collapsed={collapsed}
          isMobile={isMobile}
          onToggleCollapse={handleToggleCollapse}
          onOpenDrawer={handleOpenDrawer}
        />

        <main className="admin-shell__content">
          <div className="admin-shell__content-inner">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayoutShell;
